import React, { useState, useRef, useEffect, useMemo } from 'react'
import styles from './AudioPlayer.module.scss'
import { PlayIcon, PauseIcon, VolumeIcon } from '../CustomIcons'

const AudioPlayer = ({ src, title, artist, duration, onPlayStatusChange, shouldPause = false }) => {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [totalDuration, setTotalDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [error, setError] = useState(null)

  const normalizedSrc = useMemo(() => {
    if (!src) return ''
    
    // If it's already a full URL (http:// or https://), use it as-is
    if (typeof src === 'string' && (src.startsWith('http://') || src.startsWith('https://'))) {
      return src
    }
    
    // If it starts with /media/, prepend domain
    if (typeof src === 'string' && src.startsWith('/media/')) {
      return `https://security.ncore.kg${src}`
    }
    
    // If it starts with media/, prepend domain with /
    if (typeof src === 'string' && src.startsWith('media/')) {
      return `https://security.ncore.kg/${src}`
    }
    
    // Otherwise return as-is
    return src
  }, [src])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = () => setCurrentTime(audio.currentTime)
    const updateDuration = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setTotalDuration(audio.duration)
      }
    }
    const handlePlay = () => {
      setIsPlaying(true)
      setError(null)
      if (onPlayStatusChange) onPlayStatusChange(true)
    }
    const handlePause = () => {
      setIsPlaying(false)
      if (onPlayStatusChange) onPlayStatusChange(false)
    }
    const handleEnded = () => {
      setIsPlaying(false)
      if (onPlayStatusChange) onPlayStatusChange(false)
    }
    const handleError = (e) => {
      console.error('Audio error event:', e)
      console.error('Audio error code:', audio.error?.code, 'Message:', audio.error?.message)
      const errorCode = audio.error?.code
      let errorMessage = 'Ошибка загрузки аудио'
      if (errorCode === 1) errorMessage = 'Загрузка отменена пользователем'
      else if (errorCode === 2) errorMessage = 'Ошибка сети'
      else if (errorCode === 3) errorMessage = 'Загрузка прервана'
      else if (errorCode === 4) errorMessage = 'Формат не поддерживается'
      setError(errorMessage)
      setIsPlaying(false)
      if (onPlayStatusChange) onPlayStatusChange(false)
    }
    const handleLoadedData = () => {
      console.log('Audio loaded successfully')
      setError(null)
    }
    const handleCanPlay = () => {
      console.log('Audio can play')
      setError(null)
    }

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('loadeddata', handleLoadedData)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('durationchange', updateDuration)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('error', handleError)

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('loadeddata', handleLoadedData)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('durationchange', updateDuration)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('error', handleError)
    }
  }, [onPlayStatusChange])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    if (shouldPause && !audio.paused) {
      audio.pause()
    }
  }, [shouldPause])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    // Always update src when normalizedSrc changes
    if (normalizedSrc) {
      console.log('Setting audio src:', normalizedSrc)
      if (audio.src !== normalizedSrc) {
        audio.src = normalizedSrc
        audio.load()
      }
    }

    audio.pause()
    audio.currentTime = 0
    setCurrentTime(0)
    setTotalDuration(0)
    setIsPlaying(false)
    setError(null)
  }, [normalizedSrc])

  const togglePlay = async (e) => {
    e.stopPropagation()
    const audio = audioRef.current
    if (!audio || !normalizedSrc) {
      setError('Нет источника аудио')
      return
    }

    try {
      if (audio.paused) {
        const playPromise = audio.play()
        if (playPromise !== undefined) {
          await playPromise
        }
      } else {
        audio.pause()
      }
    } catch (err) {
      console.error('Error toggling audio playback:', err)
      setError(`Ошибка воспроизведения: ${err.message}`)
      setIsPlaying(false)
      if (onPlayStatusChange) onPlayStatusChange(false)
    }
  }

  const toggleMute = (e) => {
    e.stopPropagation()
    const audio = audioRef.current
    if (!audio) return
    audio.muted = !audio.muted
    setIsMuted(audio.muted)
  }

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value)
    const audio = audioRef.current
    setVolume(newVolume)
    if (audio) {
      audio.volume = newVolume
      if (audio.muted && newVolume > 0) {
        audio.muted = false
      }
      setIsMuted(audio.muted)
    }
  }

  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value)
    setCurrentTime(newTime)
    if (audioRef.current) {
      audioRef.current.currentTime = newTime
    }
  }

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }

  return (
    <div className={styles.audioPlayer}>
      <audio 
        ref={audioRef} 
        preload="metadata"
      />

      <div className={styles.playerHeader}>
        <div className={styles.trackInfo}>
          <h4 className={styles.trackTitle}>{title || 'Без названия'}</h4>
          {artist && <p className={styles.trackArtist}>{artist}</p>}
          {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
      </div>

      <div className={styles.playerControls}>
        <button 
          className={`${styles.playButton} ${isPlaying ? styles.playing : ''}`}
          onClick={togglePlay}
          title={isPlaying ? 'Пауза' : 'Воспроизведение'}
        >
          {isPlaying ? (
            <PauseIcon size={24} />
          ) : (
            <PlayIcon size={24} />
          )}
        </button>

        <div className={styles.progressSection}>
          <span className={styles.timeDisplay}>{formatTime(currentTime)}</span>
          <input
            type="range"
            min="0"
            max={totalDuration || 0}
            value={currentTime}
            onChange={handleProgressChange}
            className={styles.progressBar}
            title="Прогресс"
          />
          <span className={styles.timeDisplay}>{formatTime(totalDuration)}</span>
        </div>
      </div>

      <div className={styles.volumeSection}>
        <button
          className={`${styles.volumeToggle} ${isMuted ? styles.muted : ''}`}
          onClick={toggleMute}
          title={isMuted ? 'Включить звук' : 'Отключить звук'}
        >
          <VolumeIcon size={18} />
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          className={styles.volumeBar}
          title="Громкость"
        />
      </div>
    </div>
  )
}

export default AudioPlayer
